/**
 * Auth0 stub architect messages and detection (AUTH0.IMPL.1-rev4).
 * Mirrors shared/credentiallessArchitect.js — client ESM (Vite cannot import CJS shared).
 * Keep aligned: shared/__tests__/credentiallessArchitectAlignment.test.js fails on drift.
 *
 * @module client/utils/credentiallessArchitect
 */

import { getProviderMeta, resolveProviderId } from "./providerContext.js";

/**
 * @param {{ providerId?: string, provider_id?: string, credentialValidatedAt?: string | null } | null | undefined} project
 */
export function isAuth0ConnectedProject(project) {
  if (resolveProviderId(project) !== "auth0") return false;
  const at = project?.credentialValidatedAt;
  return at != null && String(at).trim() !== "";
}

/**
 * Unconnected Auth0 only — connected projects use LLM architect (AUTH0.IMPL.4-rev).
 *
 * @param {{ providerId?: string, provider_id?: string, credentialValidatedAt?: string | null } | null | undefined} project
 */
export function isAuth0StubProject(project) {
  if (resolveProviderId(project) !== "auth0") return false;
  return !isAuth0ConnectedProject(project);
}

/**
 * @param {unknown} audit
 */
export function isAuth0StubAssessment(audit) {
  if (!audit || typeof audit !== "object") return false;
  const a = /** @type {Record<string, unknown>} */ (audit);
  if (a.stubAssessment === true || a.inDevelopment === true) return true;
  const raw = a.rawData;
  if (raw && typeof raw === "object" && /** @type {Record<string, unknown>} */ (raw).stub === true) {
    return true;
  }
  const assessment = a.assessment;
  if (assessment && typeof assessment === "object") {
    const meta = /** @type {Record<string, unknown>} */ (assessment).adapter_metadata;
    if (meta && typeof meta === "object" && /** @type {Record<string, unknown>} */ (meta).auth0_stub === true) {
      return true;
    }
  }
  const pid = a.provider_id ?? a.providerId;
  if (resolveProviderId(typeof pid === "string" ? pid : null) !== "auth0") return false;
  const findings = a.findings;
  if (!findings || typeof findings !== "object") return false;
  const f = /** @type {Record<string, unknown>} */ (findings);
  const crit = Array.isArray(f.critical) ? f.critical.length : 0;
  const warn = Array.isArray(f.warnings) ? f.warnings.length : 0;
  const healthy = Array.isArray(f.healthy) ? f.healthy.length : 0;
  if (crit + warn + healthy > 0) return false;
  const inconclusive = Array.isArray(f.inconclusive) ? f.inconclusive : [];
  return inconclusive.length > 0;
}

/**
 * @param {string | null | undefined} providerId
 */
export function auth0StubBootstrapMessage(providerId) {
  const meta = getProviderMeta(providerId || "auth0");
  return (
    `Your ${meta.displayName} project is set up. Run an assessment from the command panel to see the current state — ` +
    `full provider support is in development.`
  );
}

/**
 * @param {string | null | undefined} providerId
 */
export function auth0StubPostAssessmentMessage(providerId) {
  const meta = getProviderMeta(providerId || "auth0");
  return (
    `Your ${meta.displayName} assessment ran in stub mode. Full provider support is in development; ` +
    `real findings will appear in an upcoming release.`
  );
}

/**
 * @param {string | null | undefined} providerId
 */
export function auth0StubResumeMessage(providerId) {
  const meta = getProviderMeta(providerId || "auth0");
  return (
    `Welcome back to your ${meta.displayName} project. Full provider support is in development — ` +
    `run an assessment from the command panel to see the current stub state.`
  );
}

/**
 * @param {string} message
 */
export function staticArchitectPayload(message) {
  return {
    ok: true,
    message,
    text: message,
    optionButtons: [],
    formFields: [],
    activeTask: null,
    architectureSteps: [],
    nextSteps: [],
    explainWhy: "",
    securityRecommendation: false,
    revealTenantInputs: false,
    proposedConfig: null,
    roadmapUpdate: null,
    memoryUpdate: null,
    executeAction: null,
    fromStaticCredentialless: true,
  };
}
